import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Stack,
  Chip,
  Button,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  AccountBalanceWallet,
  Payment,
  History,
  CheckCircle,
  Edit,
  Refresh,
} from '@mui/icons-material';
import { Checkbox, FormControlLabel } from '@mui/material';
import api, { apiWithRetry } from '../utils/api';
import axios from 'axios';
import { API } from '../config/apiConfig';
import { jwtDecode } from 'jwt-decode';

export default function WithdrawHub() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [user, setUser] = useState(null);
  const [countryCode, setCountryCode] = useState('');

  const [accounts, setAccounts] = useState([]); // wallet.UserPaymentAccounts
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedAccountSummary, setSelectedAccountSummary] = useState('');

  const [stripeAvailable, setStripeAvailable] = useState(false);
  const [stripeVerified, setStripeVerified] = useState(false);
  const [paypalAccount, setPaypalAccount] = useState({ name: '', email: '' });
  const [paypalSaved, setPaypalSaved] = useState(false);

  const [jazzCashNumber, setJazzCashNumber] = useState('');
  const [jazzCashCNIC, setJazzCashCNIC] = useState('');
  const [jazzCashAvailable, setJazzCashAvailable] = useState(false);
  const [jazzCashSaved, setJazzCashSaved] = useState(false);

  const [fees, setFees] = useState({ stripe: null, paypal: null, jazzcash: null });
  const [methods, setMethods] = useState([]);
  const [methodsLoading, setMethodsLoading] = useState(false);
  const [methodsLoadError, setMethodsLoadError] = useState(false);

  const [missingProfileFields, setMissingProfileFields] = useState([]);

  const [amount, setAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const [availableBalance, setAvailableBalance] = useState(0);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const [paymentTermsAccepted, setPaymentTermsAccepted] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [termsCheckboxChecked, setTermsCheckboxChecked] = useState(false);

  const [calculatedFee, setCalculatedFee] = useState(null);
  const [finalAmount, setFinalAmount] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);

  const token = useMemo(() => localStorage.getItem('token'), []);
  const userId = useMemo(() => {
    try { return token ? jwtDecode(token).userId : null; } catch { return null; }
  }, [token]);

  const requireAccountSelected = !selectedAccountId;

  useEffect(() => {
    async function bootstrap() {
      try {
        setLoading(true);
        setErrorMsg('');

        // 1) Load user basic info
        const userRes = await api.get(API.userProfile(userId));
        const usr = userRes.data || {};
        setUser(usr);
        
        // Check payment terms acceptance status (from user profile or payment settings)
        let termsAccepted = usr.PaymentTermsAccepted === true || usr.PaymentTermsAccepted === 1;
        
        // If not found in user profile, try loading from payment settings
        if (!termsAccepted) {
          try {
            const paymentSettingsRes = await api.get(API.paymentSettings);
            const paymentSettings = paymentSettingsRes.data?.data || paymentSettingsRes.data || {};
            termsAccepted = paymentSettings.PaymentTermsAccepted === true || paymentSettings.PaymentTermsAccepted === 1;
          } catch (err) {
            // If payment settings endpoint fails, default to false (user must accept)
            console.warn('Could not load payment settings:', err.message);
          }
        }
        
        setPaymentTermsAccepted(termsAccepted);

        // 2) Country code (fallback from user if available) - needed before checking postal code requirement
        const cc = (userRes.data?.CountryCode || userRes.data?.Country || '').toString().toUpperCase();
        setCountryCode(cc);
        const isPakistan = cc === 'PK';

        // Compute required profile fields (exclude RegionId)
        // Note: PostalCode is NOT required for Pakistan users (same as UpdateProfile.js validation)
        const variants = {
          SexId: ['SexId', 'sexId', 'GenderId', 'genderId'],
          Email: ['Email', 'email'],
          CountryOfOriginId: ['CountryOfOriginId', 'countryOfOriginId', 'CountryOfOriginID', 'CountryOfOrigin'],
          CountryOfResidenceId: ['CountryOfResidenceId', 'countryOfResidenceId', 'CountryOfResidenceID', 'CountryOfResidence'],
          EthnicityId: ['EthnicityId', 'ethnicityId'],
          DateOfBirth: ['DateOfBirth', 'dateOfBirth', 'DOB', 'BirthDate', 'birthDate'],
          AddressLine1: ['AddressLine1', 'addressLine1', 'Address', 'address', 'Address1'],
          City: ['City', 'city', 'Town'],
          // Only require postal code if NOT Pakistan (same logic as UpdateProfile.js line 416)
          ...(isPakistan ? {} : { PostalCode: ['PostalCode', 'postalCode', 'PostCode', 'Zip', 'ZipCode'] })
        };

        const isProvided = (obj, keys) => {
          for (const k of keys) {
            const v = obj?.[k];
            if (v !== undefined && v !== null && String(v).trim() !== '') return true;
          }
          return false;
        };

        const missing = Object.entries(variants)
          .filter(([label, keys]) => !isProvided(usr, keys))
          .map(([label]) => label);
        setMissingProfileFields(missing);

        // 3) Load existing payment accounts
        const accRes = await api.get(API.savedPaymentAccounts);
        const accs = Array.isArray(accRes.data?.accounts) ? accRes.data.accounts : [];
        setAccounts(accs);

        // 4) Load available methods (dynamic, country-aware) with retry logic for slow connections
        // OPTIMISTIC UI: For Pakistan users, show JazzCash immediately while API loads (for slow connections)
        // This ensures JazzCash is visible even on very slow connections
        if (cc === 'PK') {
          setJazzCashAvailable(true); // Show JazzCash immediately for Pakistan users (don't wait for API)
          // Load saved JazzCash account if available from already loaded accounts
          const jc = accs.find(a => (a.Provider || a.PaymentMethodName) === 'JazzCash');
          if (jc) {
            setJazzCashNumber(jc.AccountNumber || '');
            setJazzCashCNIC(jc.CNIC || '');
            setJazzCashSaved(true); // Mark as saved
          }
        }
        
        // Start loading methods in background (non-blocking for Pakistan users)
        // For Pakistan, JazzCash is already shown, so API can load slowly without blocking UI
        setMethodsLoading(true);
        setMethodsLoadError(false);
        
        const toArray = (payload) => {
          if (Array.isArray(payload)) return payload;
          if (Array.isArray(payload?.methods)) return payload.methods;
          if (Array.isArray(payload?.data)) return payload.data;
          return [];
        };

        // Helper function for API calls with extended timeout for slow connections (especially Pakistan)
        const apiCallWithExtendedTimeout = async (url, timeoutMs = 120000) => { // Increased to 120 seconds for very slow connections
          try {
            // Create a custom axios instance with extended timeout for this specific call
            const customApi = axios.create({
              baseURL: api.defaults.baseURL,
              timeout: timeoutMs,
              headers: api.defaults.headers
            });
            
            // Add auth token
            const token = localStorage.getItem('token');
            if (token) {
              customApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            }
            
            return await customApi.get(url);
          } catch (err) {
            // If timeout or connection error, try one more time with even longer timeout (180 seconds)
            if (err.message?.includes('timeout') || err.code === 'ECONNABORTED' || err.message === 'Network Error') {
              console.warn(`Request timeout/error for ${url}, retrying with extended timeout (180s)...`);
              const customApi = axios.create({
                baseURL: api.defaults.baseURL,
                timeout: 180000, // 3 minutes for very slow connections
                headers: api.defaults.headers
              });
              
              const token = localStorage.getItem('token');
              if (token) {
                customApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
              }
              
              return await customApi.get(url);
            }
            throw err;
          }
        };

        // For Pakistan users, load methods in background without blocking UI
        // JazzCash is already shown optimistically, so API can take time
        const loadPaymentMethodsAsync = async () => {
          let available = [];
          let methodsLoadSuccess = false;
          
          try {
            // Try with extended timeout for slow connections
            const res1 = await apiCallWithExtendedTimeout(API.paymentMethodsForUser);
            available = toArray(res1.data);
            if (available.length > 0) methodsLoadSuccess = true;
          } catch (err) {
            console.warn('Payment methods API call failed (will retry):', err.message);
          }
          
          // Fallbacks with extended timeout
          if (available.length === 0 && cc) {
            try {
              const res2 = await apiCallWithExtendedTimeout(API.paymentMethodsForCountryCode(cc));
              available = toArray(res2.data);
              if (available.length > 0) methodsLoadSuccess = true;
            } catch (err) {
              console.warn('Country payment methods API call failed:', err.message);
            }
          }
          if (available.length === 0) {
            try {
              const res3 = await apiCallWithExtendedTimeout(API.paymentMethods);
              available = toArray(res3.data);
              if (available.length > 0) methodsLoadSuccess = true;
            } catch (err) {
              console.warn('Payment methods fallback API call failed:', err.message);
            }
          }
          if (available.length === 0) {
            try {
              const res4 = await apiCallWithExtendedTimeout(API.availablePaymentMethods);
              available = toArray(res4.data);
              if (available.length > 0) methodsLoadSuccess = true;
            } catch (err) {
              console.warn('Available payment methods API call failed:', err.message);
            }
          }
          
          // Final fallback: load by country if user-based endpoint returns empty
          if (available.length === 0 && cc) {
            try {
              const byCountryRes = await apiCallWithExtendedTimeout(API.paymentMethodsForCountryCode(cc));
              const countryMethods = Array.isArray(byCountryRes.data?.methods) ? byCountryRes.data.methods : [];
              if (countryMethods.length > 0) {
                available = countryMethods;
                methodsLoadSuccess = true;
              }
            } catch (err) {
              console.warn('Final country payment methods API call failed:', err.message);
            }
          }
          
          setMethods(available);
          setMethodsLoading(false);
          setMethodsLoadError(!methodsLoadSuccess && available.length === 0);
          
          return { available, methodsLoadSuccess };
        };
        
        // For Pakistan users, don't wait for API - show JazzCash immediately
        // Load methods in background (non-blocking)
        let available = [];
        if (cc === 'PK') {
          // For Pakistan: Show JazzCash immediately, load everything else in background
          // This ensures JazzCash is visible even on very slow connections
          setMethodsLoading(false); // Don't show loading state - JazzCash is already visible
          
          // Start loading in background, but don't block UI - JazzCash is already shown
          loadPaymentMethodsAsync().then(({ available: loadedMethods }) => {
            // Update methods when loaded, but JazzCash is already shown
            setMethods(loadedMethods);
            const hasJazzCash = loadedMethods.some(m => (m.PaymentMethodName || m.name) === 'JazzCash');
            if (hasJazzCash) {
              const jc = accs.find(a => (a.Provider || a.PaymentMethodName) === 'JazzCash');
              if (jc && !jazzCashNumber) {
                setJazzCashNumber(jc.AccountNumber || '');
                setJazzCashCNIC(jc.CNIC || '');
                setJazzCashSaved(true); // Mark as saved
              }
            }
            
            // Load fees after methods are loaded (in background, non-blocking)
            loadFeesAsync(loadedMethods).catch(err => {
              console.warn('Fee loading failed (non-critical, JazzCash still works):', err.message);
            });
          }).catch(err => {
            console.warn('Background payment methods load failed (JazzCash still shown and functional):', err.message);
            setMethodsLoading(false);
            // Even if API fails, JazzCash is still shown and functional
            // Try to load fees with empty methods (will use defaults or show "—")
            loadFeesAsync([]).catch(() => {});
          });
          
          // Set initial methods to empty array so we don't block UI
          // JazzCash is already shown optimistically, so user can interact immediately
          setMethods([]);
          available = []; // Empty for now, will be updated when API loads
          
          // Set initial fees to empty (will be loaded in background)
          setFees({ stripe: null, paypal: null, jazzcash: null });
        } else {
          // For non-Pakistan users, wait for methods to load
          const result = await loadPaymentMethodsAsync();
          available = result.available;
          setMethods(available);
        }
        
        const hasStripe = available.some(m => (m.PaymentMethodName || m.name) === 'Stripe');
        const hasPayPal = available.some(m => (m.PaymentMethodName || m.name) === 'PayPal');
        const hasJazzCash = available.some(m => (m.PaymentMethodName || m.name) === 'JazzCash');
        
        // Fallback: if no methods provided, assume Stripe and PayPal are globally available (except Stripe hidden in PK)
        setStripeAvailable(available.length === 0 ? cc !== 'PK' : hasStripe);

        // Stripe verification status (with retry for slow connections) - non-blocking for Pakistan
        if (cc !== 'PK') {
          try {
            const kycRes = await apiWithRetry.get(API.kycStatus);
            setStripeVerified(!!kycRes.data?.verified);
          } catch (err) {
            console.warn('KYC status check failed:', err.message);
          }
        } else {
          // For Pakistan, load KYC status in background (non-blocking)
          apiWithRetry.get(API.kycStatus).then(kycRes => {
            setStripeVerified(!!kycRes.data?.verified);
          }).catch(() => {});
        }

        // PayPal
        const pp = accs.find(a => (a.Provider || a.PaymentMethodName) === 'PayPal');
        if (pp) {
          setPaypalSaved(true);
          setPaypalAccount({ name: pp.AccountName || '', email: pp.AccountNumber || pp.AccountEmail || '' });
        }

        // JazzCash availability - Always show for Pakistan users (optimistic UI)
        // If API loaded successfully and found JazzCash, keep it; otherwise keep optimistic state
        if (cc === 'PK') {
          // Always show JazzCash for Pakistan users, even if API fails or is slow
          setJazzCashAvailable(true);
          // If API loaded and found JazzCash account, update it (but don't wait)
          if (hasJazzCash) {
            const jc = accs.find(a => (a.Provider || a.PaymentMethodName) === 'JazzCash');
            if (jc && !jazzCashNumber) {
              setJazzCashNumber(jc.AccountNumber || '');
            }
          }
        } else {
          setJazzCashAvailable(hasJazzCash);
          if (hasJazzCash) {
            const jc = accs.find(a => (a.Provider || a.PaymentMethodName) === 'JazzCash');
            if (jc) setJazzCashNumber(jc.AccountNumber || '');
          }
        }

        // 5) Fees from methods if provided (only for non-Pakistan users, Pakistan loads in background)
        if (cc !== 'PK') {
          const feeFrom = (name) => {
            const m = available.find(x => (x.PaymentMethodName || x.name) === name);
            if (!m) return null;
            if (m.FeeDescription || m.fee) return m.FeeDescription || m.fee;
            const pct = m.processingFeePercent ?? m.processingFeePercentage ?? m.ProcessingFeePercent ?? m.ProcessingFeePercentage ?? m.LocalProcessingFeePercent;
            const fixed = m.processingFeeFixed ?? m.ProcessingFeeFixed ?? m.LocalProcessingFeeFixed ?? m.processingFeeFixedLocal;
            if (pct != null || fixed != null) {
              // percent may already be in percent (e.g., 2.5) or fraction (0.025)
              let pctNum = parseFloat(pct);
              if (pctNum > 0 && pctNum < 1) pctNum = pctNum * 100;
              const pctTxt = !isNaN(pctNum) ? `${pctNum.toString()}%` : null;
              const fixedTxt = fixed != null ? `${parseFloat(fixed).toFixed(2)}` : null;
              if (pctTxt && fixedTxt) return `${pctTxt} + ${fixedTxt}`;
              return pctTxt || fixedTxt;
            }
            return null;
          };
          let nextFees = { stripe: feeFrom('Stripe'), paypal: feeFrom('PayPal'), jazzcash: feeFrom('JazzCash') };

          // If any fee missing, fetch estimate via /fees/calculate for amount 100
          const ensureFee = async (methodName, key) => {
            if (nextFees[key]) return;
            const m = available.find(x => (x.PaymentMethodName || x.name) === methodName);
            if (!m) return;
            try {
              const payload = {
                amount: 100,
                paymentMethodId: m.PaymentMethodId || m.id,
              };
              const resp = await api.post(API.calculateFees, payload);
              const data = resp.data || {};
              if (data.totalFees != null || data.fees?.totalFees != null) {
                const total = parseFloat(data.totalFees ?? data.fees?.totalFees).toFixed(2);
                nextFees[key] = `~${total} on 100`;
              }
            } catch (_) {}
          };

          await ensureFee('Stripe', 'stripe');
          await ensureFee('PayPal', 'paypal');
          await ensureFee('JazzCash', 'jazzcash');

          setFees(nextFees);
        }
        
        // Helper function to load fees asynchronously (for Pakistan background loading)
        async function loadFeesAsync(methodsArray) {
          const feeFrom = (name) => {
            const m = methodsArray.find(x => (x.PaymentMethodName || x.name) === name);
            if (!m) return null;
            if (m.FeeDescription || m.fee) return m.FeeDescription || m.fee;
            const pct = m.processingFeePercent ?? m.processingFeePercentage ?? m.ProcessingFeePercent ?? m.ProcessingFeePercentage ?? m.LocalProcessingFeePercent;
            const fixed = m.processingFeeFixed ?? m.ProcessingFeeFixed ?? m.LocalProcessingFeeFixed ?? m.processingFeeFixedLocal;
            if (pct != null || fixed != null) {
              let pctNum = parseFloat(pct);
              if (pctNum > 0 && pctNum < 1) pctNum = pctNum * 100;
              const pctTxt = !isNaN(pctNum) ? `${pctNum.toString()}%` : null;
              const fixedTxt = fixed != null ? `${parseFloat(fixed).toFixed(2)}` : null;
              if (pctTxt && fixedTxt) return `${pctTxt} + ${fixedTxt}`;
              return pctTxt || fixedTxt;
            }
            return null;
          };
          
          let nextFees = { stripe: feeFrom('Stripe'), paypal: feeFrom('PayPal'), jazzcash: feeFrom('JazzCash') };

          const ensureFee = async (methodName, key) => {
            if (nextFees[key]) return;
            const m = methodsArray.find(x => (x.PaymentMethodName || x.name) === methodName);
            if (!m) return;
            try {
              const payload = {
                amount: 100,
                paymentMethodId: m.PaymentMethodId || m.id,
              };
              const resp = await api.post(API.calculateFees, payload);
              const data = resp.data || {};
              if (data.totalFees != null || data.fees?.totalFees != null) {
                const total = parseFloat(data.totalFees ?? data.fees?.totalFees).toFixed(2);
                nextFees[key] = `~${total} on 100`;
              }
            } catch (_) {}
          };

          await ensureFee('Stripe', 'stripe');
          await ensureFee('PayPal', 'paypal');
          await ensureFee('JazzCash', 'jazzcash');

          setFees(nextFees);
        }

        // 6) History
        await loadHistory();
        
        // 6) Load wallet balance
        await loadBalance();

      } catch (err) {
        setErrorMsg(err.response?.data?.error || err.message || 'Failed to load withdrawal data');
      } finally {
        setLoading(false);
      }
    }
    if (userId) bootstrap();
  }, [userId]);

  async function loadBalance() {
    try {
      setBalanceLoading(true);
      const res = await api.get(API.walletBalance);
      if (res.data?.success) {
        setAvailableBalance(res.data.balance || 0);
      }
    } catch (err) {
      console.error('Error loading balance:', err);
      setAvailableBalance(0);
    } finally {
      setBalanceLoading(false);
    }
  }

  async function loadHistory() {
    try {
      setHistoryLoading(true);
      const res = await api.get(API.withdrawalHistory);
      setHistory(Array.isArray(res.data?.history) ? res.data.history : []);
    } catch (err) {
      // non-fatal
    } finally {
      setHistoryLoading(false);
    }
  }

  function selectAccount(account) {
    setSelectedAccountId(account?.AccountID || account?.PaymentAccountID || account?.id);
    const label = `${account?.PaymentMethodName || account?.Provider || 'Account'} • ${account?.AccountName || ''}`.trim();
    setSelectedAccountSummary(label);
  }

  async function savePaypal() {
    try {
      setErrorMsg('');
      const method = methods.find(m => (m.PaymentMethodName || m.name) === 'PayPal');
      if (!method) throw new Error('PayPal not available');
      
      // Find existing PayPal account for this user
      const existingPayPalAccount = accounts.find(
        acc => (acc.PaymentMethodName || acc.Provider) === 'PayPal'
      );
      
      await api.post(API.userPaymentAccounts, {
        accountId: existingPayPalAccount?.AccountID || existingPayPalAccount?.PaymentAccountID || existingPayPalAccount?.id || null,
        userId: user?.UserID || user?.id,
        paymentMethodId: method.PaymentMethodId || method.id,
        accountName: paypalAccount.name || 'PayPal',
        accountNumber: paypalAccount.email,
        isDefault: true,
      });
      setPaypalSaved(true);
      const refreshed = await api.get(API.savedPaymentAccounts);
      setAccounts(refreshed.data?.accounts || []);
    } catch (err) {
      setErrorMsg(err.response?.data?.error || err.message || 'Failed to save PayPal');
    }
  }

  async function saveJazzCash() {
    try {
      setErrorMsg('');
      
      // For Pakistan users, if methods haven't loaded yet due to slow connection,
      // try to fetch JazzCash method on-demand
      let method = methods.find(m => (m.PaymentMethodName || m.name) === 'JazzCash');
      
      if (!method && countryCode === 'PK') {
        try {
          console.log('JazzCash method not loaded yet, fetching on-demand for Pakistan user...');
          // Create a custom axios instance with extended timeout for slow connections
          const customApi = axios.create({
            baseURL: api.defaults.baseURL,
            timeout: 120000, // 2 minutes for very slow connections
            headers: api.defaults.headers
          });
          
          const token = localStorage.getItem('token');
          if (token) {
            customApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          }
          
          const methodRes = await customApi.get(API.paymentMethodsForCountryCode('PK'));
          const methodList = Array.isArray(methodRes.data) ? methodRes.data : 
                           Array.isArray(methodRes.data?.methods) ? methodRes.data.methods :
                           Array.isArray(methodRes.data?.data) ? methodRes.data.data : [];
          method = methodList.find(m => (m.PaymentMethodName || m.name) === 'JazzCash');
          
          // Update methods state if found
          if (method) {
            setMethods(prev => {
              const filtered = prev.filter(m => (m.PaymentMethodName || m.name) !== 'JazzCash');
              return [...filtered, method];
            });
          }
        } catch (err) {
          console.warn('Failed to load JazzCash method on-demand:', err.message);
          // Continue anyway - backend might accept the request without method ID
        }
      }
      
      if (!method) {
        // For Pakistan users, try to save anyway - backend might handle it
        // Otherwise, show error
        if (countryCode !== 'PK') {
          throw new Error('JazzCash not available. Please refresh the page and try again.');
        }
        // For Pakistan, try with a fallback - backend should handle JazzCash for PK
        console.warn('JazzCash method not found, attempting save anyway for Pakistan user...');
      }
      
      // Validate CNIC is provided and is exactly 13 digits
      const cnicTrimmed = jazzCashCNIC?.trim() || '';
      if (!cnicTrimmed) {
        throw new Error('CNIC is required for JazzCash accounts');
      }
      // Remove any non-numeric characters and validate length
      const cnicNumbersOnly = cnicTrimmed.replace(/\D/g, '');
      if (cnicNumbersOnly.length !== 13) {
        throw new Error('CNIC must be exactly 13 digits');
      }

      // ✅ Check for existing JazzCash account (only one account per payment method)
      const existingJazzCashAccount = accounts.find(
        acc => (acc.PaymentMethodName || acc.Provider) === 'JazzCash'
      );

      await api.post(API.userPaymentAccounts, {
        accountId: existingJazzCashAccount?.AccountID || existingJazzCashAccount?.PaymentAccountID || existingJazzCashAccount?.id || null,
        userId: user?.UserID || user?.id,
        paymentMethodId: method?.PaymentMethodId || method?.id,
        accountName: 'JazzCash',
        accountNumber: jazzCashNumber,
        cnic: cnicNumbersOnly, // Use numbers-only version
        isDefault: true,
      });
      
      const refreshed = await api.get(API.savedPaymentAccounts);
      setAccounts(refreshed.data?.accounts || []);
      setJazzCashSaved(true); // Mark as saved after successful save
      setSuccessMsg('JazzCash account saved successfully!');
    } catch (err) {
      setErrorMsg(err.response?.data?.error || err.message || 'Failed to save JazzCash. Please try again.');
    }
  }

  async function startStripeVerification() {
    try {
      const res = await api.post(API.kycStart);
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      setErrorMsg(err.response?.data?.error || err.message || 'Failed to start Stripe verification');
    }
  }

  async function acceptPaymentTerms() {
    try {
      setAcceptingTerms(true);
      setErrorMsg('');
      const res = await api.post(API.acceptPaymentTerms, { accepted: true });
      if (res.data?.success) {
        setPaymentTermsAccepted(true);
        setSuccessMsg('Payment terms accepted successfully. You can now request redemptions.');
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.error || err.message || 'Failed to accept payment terms. Please try again.');
    } finally {
      setAcceptingTerms(false);
    }
  }

  // Calculate fees when amount changes (for JazzCash and PayPal)
  useEffect(() => {
    const calculateFee = async () => {
      if (!amount || parseFloat(amount) <= 0) {
        setCalculatedFee(null);
        setFinalAmount(null);
        return;
      }

      const requestAmount = parseFloat(amount);
      let fee = 0;
      let final = requestAmount;

      // Calculate fee based on selected payment method
      if (countryCode === 'PK' && selectedAccountSummary.startsWith('JazzCash')) {
        // JazzCash fee: 0.03% of the withdrawal amount (Pakistani users only)
        const feePercent = 0.0003; // 0.03%
        fee = requestAmount * feePercent;
        final = requestAmount - fee;
      } else if (selectedAccountSummary.startsWith('PayPal')) {
        // PayPal Payouts API: $0.25 flat fee (US domestic) or varies by currency (international)
        // For now, using $0.25 as default (actual may vary by country/currency)
        fee = 0.25;
        final = requestAmount - fee;
      } else {
        // Other payment methods - no calculation for now
        setCalculatedFee(null);
        setFinalAmount(null);
        return;
      }

      setCalculatedFee(fee);
      setFinalAmount(final);
    };

    calculateFee();
  }, [amount, countryCode, selectedAccountSummary]);

  async function submitWithdraw() {
    let withdrawalPayload = null; // Declare outside try block for error logging
    try {
      setWithdrawing(true);
      setErrorMsg('');
      setSuccessMsg('');
      if (!selectedAccountId) {
        setErrorMsg('Please select a payout account first.');
        return;
      }
      if (!amount || parseFloat(amount) <= 0) {
        setErrorMsg('Please enter a valid amount.');
        return;
      }
      
      const requestAmount = parseFloat(amount);
      if (requestAmount > availableBalance) {
        setErrorMsg(`Insufficient balance. Available: $${availableBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}, Requested: $${requestAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
        return;
      }
      
      // Find the selected account to determine payment method
      let selectedAccount = accounts.find(
        acc => (acc.AccountID || acc.PaymentAccountID || acc.id) === selectedAccountId
      ) || {};
      
      // Handle placeholder IDs for PayPal, Stripe, JazzCash
      // If using placeholder, find the actual saved account
      if (!selectedAccount.AccountID && !selectedAccount.PaymentAccountID && !selectedAccount.id) {
        if (selectedAccountId === 'paypal' && paypalSaved) {
          selectedAccount = accounts.find(
            acc => (acc.PaymentMethodName || acc.Provider) === 'PayPal'
          ) || {};
        } else if (selectedAccountId === 'stripe') {
          selectedAccount = accounts.find(
            acc => (acc.PaymentMethodName || acc.Provider) === 'Stripe'
          ) || {};
        } else if (selectedAccountId === 'jazzcash' && jazzCashAvailable) {
          selectedAccount = accounts.find(
            acc => (acc.PaymentMethodName || acc.Provider) === 'JazzCash'
          ) || {};
        }
      }
      
      // Use the actual account ID if we found it, otherwise use placeholder
      const actualAccountId = selectedAccount.AccountID || 
                             selectedAccount.PaymentAccountID || 
                             selectedAccount.id || 
                             selectedAccountId;
      
      // Determine payment method from selected account or summary
      const paymentMethod = selectedAccount.PaymentMethodName || 
                           selectedAccount.Provider || 
                           (selectedAccountSummary.startsWith('PayPal') ? 'PayPal' :
                            selectedAccountSummary.startsWith('Stripe') ? 'Stripe' :
                            selectedAccountSummary.startsWith('JazzCash') ? 'JazzCash' : null);
      
      // IMPORTANT: PayPal and Stripe payouts MUST always be in USD for global users
      // This ensures users worldwide receive USD payments regardless of their country
      const currencyCode = (paymentMethod === 'PayPal' || paymentMethod === 'Stripe') ? 'USD' : undefined;
      
      // Validate that we have a valid account ID (not a placeholder string)
      const isPlaceholderId = typeof actualAccountId === 'string' && 
                              ['paypal', 'stripe', 'jazzcash'].includes(actualAccountId.toLowerCase());
      const hasValidAccount = selectedAccount.AccountID || selectedAccount.PaymentAccountID || selectedAccount.id;
      
      if (isPlaceholderId && !hasValidAccount) {
        setErrorMsg('Please save your payment account details before requesting withdrawal.');
        return;
      }
      
      if (!actualAccountId) {
        setErrorMsg('Please select a valid payment account.');
        return;
      }
      
      // 🔍 DEBUG: Log withdrawal request payload for testing
      withdrawalPayload = {
        amount: parseFloat(amount),
        paymentAccountId: actualAccountId,
        ...(currencyCode ? { currencyCode } : {}), // Only include currencyCode if explicitly USD
      };
      
      console.log('🔍 [WithdrawHub] Withdrawal Request Payload:', JSON.stringify({
        ...withdrawalPayload,
        paymentMethod: paymentMethod || 'Unknown',
        selectedAccount: {
          id: selectedAccount.AccountID || selectedAccount.PaymentAccountID || selectedAccount.id,
          name: selectedAccount.AccountName,
          method: selectedAccount.PaymentMethodName || selectedAccount.Provider
        },
        currencyEnforcement: {
          detectedMethod: paymentMethod,
          isPayPalOrStripe: paymentMethod === 'PayPal' || paymentMethod === 'Stripe',
          currencyCode: currencyCode || 'Not specified (will use default)'
        }
      }, null, 2));
      
      const res = await api.post(API.withdrawalRequest, withdrawalPayload);
      setSuccessMsg(res.data?.message || 'Redemption requested successfully');
      setAmount('');
      await loadHistory();
      await loadBalance(); // Refresh balance after withdrawal
    } catch (err) {
      // Enhanced error logging to help debug 400 errors
      const errorDetails = {
        status: err.response?.status,
        statusText: err.response?.statusText,
        error: err.response?.data?.error,
        message: err.response?.data?.message,
        details: err.response?.data?.details,
        fullResponse: err.response?.data,
        requestPayload: withdrawalPayload
      };
      console.error('❌ [WithdrawHub] Withdrawal Request Error:', JSON.stringify(errorDetails, null, 2));
      console.error('❌ Error Status:', err.response?.status);
      console.error('❌ Error Message:', err.response?.data?.error || err.response?.data?.message || err.message);
      console.error('❌ Full Error Response:', err.response?.data);
      
      // Show detailed error message to user
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          err.response?.data?.details ||
                          (err.response?.status === 400 ? 'Invalid request. Please check your account details and try again.' : '') ||
                          err.message || 
                          'Redemption request failed';
      
      setErrorMsg(errorMessage);
    } finally {
      setWithdrawing(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
      <Card sx={{ mb: { xs: 2, sm: 3 }, borderRadius: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Typography 
            variant="h5" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
              mb: { xs: 0.5, sm: 1 }
            }}
          >
            <AccountBalanceWallet sx={{ fontSize: { xs: '20px', sm: '24px' } }} /> 
            Redemptions
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            Manage payout accounts, request redemptions, and review history in one place.
          </Typography>
        </CardContent>
      </Card>

      {/* Profile completion gate */}
      {missingProfileFields.length > 0 && (
        <Card sx={{ mb: 3, border: '1px dashed', borderColor: 'warning.main' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="h6" 
              color="warning.main" 
              sx={{ 
                mb: 1,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                fontWeight: 600
              }}
            >
              Complete your profile to enable redemptions
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 2,
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                wordBreak: 'break-word'
              }}
            >
              Please provide the following required fields: {missingProfileFields.join(', ')}.
            </Typography>
            <Button 
              variant="contained" 
              color="warning" 
              onClick={() => navigate('/dashboard/profile')}
              fullWidth={{ xs: true, sm: false }}
              sx={{ 
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: 'auto', sm: 120 }
              }}
            >
              Go to Profile
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Payment Terms & Liability Agreement */}
      {!paymentTermsAccepted && (
        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'warning.main', bgcolor: 'rgba(255, 152, 0, 0.05)' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="h6" 
              color="warning.main" 
              sx={{ 
                mb: 2,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                fontWeight: 600
              }}
            >
              Payment Terms & Liability Agreement
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 2,
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                lineHeight: 1.6
              }}
            >
              Before requesting redemptions, you must acknowledge and agree to our Payment Terms & Liability Agreement.
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowTermsDialog(true)}
                sx={{ mb: 2 }}
              >
                View Full Terms & Conditions
              </Button>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsCheckboxChecked}
                  onChange={(e) => setTermsCheckboxChecked(e.target.checked)}
                  disabled={acceptingTerms}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
                  I have read and agree to the Payment Terms & Liability Agreement
                </Typography>
              }
            />
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={acceptPaymentTerms}
                disabled={!termsCheckboxChecked || acceptingTerms}
                fullWidth
              >
                {acceptingTerms ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Accepting Terms...
                  </>
                ) : (
                  'Accept Terms & Continue'
                )}
              </Button>
            </Box>
            {acceptingTerms && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Accepting terms...
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Select Account - Show JazzCash only for Pakistan, Stripe/PayPal for others */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" sx={{ mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Select Account</Typography>
          <Divider sx={{ mb: 2 }} />

          <Stack spacing={2}>
            {/* Stripe - Hidden as not integrated yet */}
            {false && stripeAvailable && countryCode !== 'PK' && (
              <Box 
                onClick={() => {
                  if (!missingProfileFields.length) {
                    selectAccount({ PaymentMethodName: 'Stripe', AccountName: 'Stripe', id: 'stripe' });
                  }
                }}
                sx={{ 
                  p: 2, 
                  border: '2px solid', 
                  borderColor: selectedAccountSummary.startsWith('Stripe') ? 'primary.main' : 'divider', 
                  borderRadius: 2,
                  cursor: missingProfileFields.length > 0 ? 'not-allowed' : 'pointer',
                  bgcolor: selectedAccountSummary.startsWith('Stripe') ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: missingProfileFields.length > 0 ? 'divider' : 'primary.main',
                    bgcolor: missingProfileFields.length > 0 ? 'transparent' : 'action.hover',
                  }
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                  <Stack spacing={0.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>Stripe</Typography>
                    <Typography variant="caption" color="text.secondary">Fee: {fees.stripe || '—'}</Typography>
                    <Typography variant="caption" color={stripeVerified ? 'success.main' : 'warning.main'}>
                      {stripeVerified ? 'Verified' : 'Not verified'}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, sm: 0 }, width: { xs: '100%', sm: 'auto' } }}>
                    {!stripeVerified && (
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          startStripeVerification();
                        }} 
                        disabled={missingProfileFields.length > 0}
                        sx={{ flex: { xs: 1, sm: 'none' } }}
                      >
                        Get verified
                      </Button>
                    )}
                    {selectedAccountSummary.startsWith('Stripe') && (
                      <CheckCircle color="primary" />
                    )}
                  </Stack>
                </Stack>
              </Box>
            )}

            {/* PayPal - global, but NOT for Pakistan */}
            {countryCode !== 'PK' && (
            <Box 
              onClick={() => {
                if (!missingProfileFields.length) {
                  // If PayPal is saved, use the actual saved account, otherwise use placeholder
                  if (paypalSaved) {
                    const savedPayPalAccount = accounts.find(
                      acc => (acc.PaymentMethodName || acc.Provider) === 'PayPal'
                    );
                    if (savedPayPalAccount) {
                      selectAccount(savedPayPalAccount);
                    } else {
                      selectAccount({ PaymentMethodName: 'PayPal', AccountName: paypalAccount.name || 'PayPal', id: 'paypal' });
                    }
                  } else {
                    selectAccount({ PaymentMethodName: 'PayPal', AccountName: paypalAccount.name || 'PayPal', id: 'paypal' });
                  }
                }
              }}
              sx={{ 
                p: 2, 
                border: '2px solid', 
                borderColor: selectedAccountSummary.startsWith('PayPal') ? 'primary.main' : 'divider', 
                borderRadius: 2,
                cursor: missingProfileFields.length > 0 ? 'not-allowed' : 'pointer',
                bgcolor: selectedAccountSummary.startsWith('PayPal') ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: missingProfileFields.length > 0 ? 'divider' : 'primary.main',
                  bgcolor: missingProfileFields.length > 0 ? 'transparent' : 'action.hover',
                }
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                <Stack spacing={0.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>PayPal</Typography>
                    <Typography variant="caption" color="text.secondary">Fee: $0.25 flat fee (Payouts API)</Typography>
                  {paypalSaved ? (
                    <Typography variant="caption" color="success.main" sx={{ wordBreak: 'break-word' }}>
                      <CheckCircle sx={{ fontSize: 14, mr: 0.5 }} /> {paypalAccount.name} • {paypalAccount.email}
                    </Typography>
                  ) : (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} onClick={(e) => e.stopPropagation()} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      <TextField size="small" label="Account name" value={paypalAccount.name} onChange={e => setPaypalAccount(v => ({ ...v, name: e.target.value }))} disabled={missingProfileFields.length > 0} sx={{ width: { xs: '100%', sm: 'auto' } }} />
                      <TextField size="small" label="Email" value={paypalAccount.email} onChange={e => setPaypalAccount(v => ({ ...v, email: e.target.value }))} disabled={missingProfileFields.length > 0} sx={{ width: { xs: '100%', sm: 'auto' } }} />
                      <Button variant="outlined" size="small" onClick={(e) => {
                        e.stopPropagation();
                        savePaypal();
                      }} disabled={missingProfileFields.length > 0} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        Save
                      </Button>
                    </Stack>
                  )}
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, sm: 0 }, width: { xs: '100%', sm: 'auto' } }}>
                  {paypalSaved && (
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setPaypalSaved(false);
                      }} 
                      aria-label="edit"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  )}
                  {selectedAccountSummary.startsWith('PayPal') && (
                    <CheckCircle color="primary" />
                  )}
                </Stack>
              </Stack>
            </Box>
            )}

            {/* JazzCash - PK only */}
            {jazzCashAvailable && (
              <Box 
                onClick={() => {
                  if (!missingProfileFields.length && jazzCashNumber) {
                    selectAccount({ PaymentMethodName: 'JazzCash', AccountName: 'JazzCash', id: 'jazzcash' });
                  }
                }}
                sx={{ 
                  p: 2, 
                  border: '2px solid', 
                  borderColor: selectedAccountSummary.startsWith('JazzCash') ? 'primary.main' : 'divider', 
                  borderRadius: 2,
                  cursor: (missingProfileFields.length > 0 || !jazzCashNumber) ? 'default' : 'pointer',
                  bgcolor: selectedAccountSummary.startsWith('JazzCash') ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: (missingProfileFields.length > 0 || !jazzCashNumber) ? 'divider' : 'primary.main',
                    bgcolor: (missingProfileFields.length > 0 || !jazzCashNumber) ? 'transparent' : 'action.hover',
                  }
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                  <Stack spacing={0.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>JazzCash</Typography>
                    <Typography variant="caption" color="text.secondary">Fee: 0.03% of withdrawal amount</Typography>
                    {jazzCashSaved ? (
                      <Typography variant="caption" color="success.main" sx={{ wordBreak: 'break-word' }}>
                        <CheckCircle sx={{ fontSize: 14, mr: 0.5 }} /> {jazzCashNumber} • CNIC: {jazzCashCNIC}
                      </Typography>
                    ) : (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} onClick={(e) => e.stopPropagation()} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <TextField 
                          size="small" 
                          label="Account number" 
                          value={jazzCashNumber} 
                          onChange={e => setJazzCashNumber(e.target.value)} 
                          disabled={missingProfileFields.length > 0} 
                          required
                          sx={{ width: { xs: '100%', sm: 'auto' } }} 
                        />
                        <TextField 
                          size="small" 
                          label="CNIC" 
                          value={jazzCashCNIC} 
                          onChange={e => {
                            // Only allow numbers, limit to 13 digits
                            const value = e.target.value.replace(/\D/g, '').slice(0, 13);
                            setJazzCashCNIC(value);
                          }} 
                          disabled={missingProfileFields.length > 0} 
                          required
                          placeholder="1234512345671"
                          inputProps={{ 
                            maxLength: 13,
                            inputMode: 'numeric',
                            pattern: '[0-9]*'
                          }}
                          helperText={jazzCashCNIC && jazzCashCNIC.length !== 13 ? 'CNIC must be exactly 13 digits' : ''}
                          error={jazzCashCNIC ? jazzCashCNIC.length !== 13 : false}
                          sx={{ width: { xs: '100%', sm: 'auto' } }} 
                        />
                        <Button 
                          variant="outlined" 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            saveJazzCash();
                          }} 
                          disabled={missingProfileFields.length > 0 || !jazzCashNumber || !jazzCashCNIC || jazzCashCNIC.length !== 13}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          Save
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, sm: 0 }, width: { xs: '100%', sm: 'auto' } }}>
                    {jazzCashSaved && (
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setJazzCashSaved(false);
                        }} 
                        aria-label="edit"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    )}
                    {selectedAccountSummary.startsWith('JazzCash') && (
                      <CheckCircle color="primary" />
                    )}
                  </Stack>
                </Stack>
              </Box>
            )}

            {/* Already saved accounts list (chips) */}
            {accounts.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {accounts.map(acc => (
                  <Chip
                    key={(acc.AccountID || acc.PaymentAccountID || acc.id) + ''}
                    label={(acc.PaymentMethodName || acc.Provider) + (acc.AccountName ? ` • ${acc.AccountName}` : '')}
                    onClick={() => selectAccount(acc)}
                    color={selectedAccountId === (acc.AccountID || acc.PaymentAccountID || acc.id) ? 'primary' : 'default'}
                    variant={selectedAccountId === (acc.AccountID || acc.PaymentAccountID || acc.id) ? 'filled' : 'outlined'}
                    clickable
                  />
                ))}
              </Stack>
            )}

            {selectedAccountSummary && (
              <Typography variant="caption" color="primary.main">Selected: {selectedAccountSummary}</Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Withdraw Amount - Hidden for Pakistan users */}
      {countryCode !== 'PK' && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Payment /> Withdraw Funds
              </Typography>
              <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                {balanceLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Available Balance
                    </Typography>
                    <Typography 
                      variant="h6" 
                      color="primary.main" 
                      sx={{ 
                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        fontWeight: 600
                      }}
                    >
                      ${availableBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </>
                )}
              </Box>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {selectedAccountSummary.startsWith('PayPal') && !paypalSaved && (
              <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
                Please save your PayPal account details above before requesting a withdrawal.
              </Alert>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <TextField
                label="Amount"
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                inputProps={{ min: 0, max: availableBalance, step: '0.01' }}
                disabled={missingProfileFields.length > 0}
                error={amount && parseFloat(amount) > availableBalance}
                helperText={
                  amount && parseFloat(amount) > availableBalance 
                    ? `Amount exceeds available balance ($${availableBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
                    : ''
                }
                sx={{ 
                  maxWidth: { xs: '100%', sm: 260 }, 
                  width: { xs: '100%', sm: 'auto' },
                  '& .MuiInputBase-root': {
                    fontSize: { xs: '0.9375rem', sm: '1rem' }
                  }
                }}
              />
              {/* Fee Calculation Display for JazzCash (Pakistani users) and PayPal */}
              {amount && parseFloat(amount) > 0 && calculatedFee !== null && (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'info.light', 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'info.main',
                  minWidth: { xs: '100%', sm: 280 }
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Fee Calculation:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Requested: ${parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Fee {selectedAccountSummary.startsWith('JazzCash') ? '(0.03%)' : '(PayPal Payouts API)'}: ${calculatedFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    You'll Receive: ${finalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
              )}
              <Button
                variant="contained"
                disabled={withdrawing || requireAccountSelected || !amount || Number(amount) <= 0 || Number(amount) > availableBalance || missingProfileFields.length > 0 || (selectedAccountSummary.startsWith('PayPal') && !paypalSaved) || !paymentTermsAccepted}
                onClick={submitWithdraw}
                fullWidth={{ xs: true, sm: false }}
                sx={{ 
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { xs: 'auto', sm: 160 },
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  py: { xs: 1.25, sm: 1.5 }
                }}
              >
                {withdrawing ? <CircularProgress size={22} color="inherit" /> : 'Request Redemption'}
              </Button>
            </Stack>
            {/* Error and Success Messages - Displayed right below redemption box */}
            {errorMsg && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setErrorMsg('')}>
                {errorMsg}
              </Alert>
            )}
            {successMsg && (
              <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccessMsg('')}>
                {successMsg}
              </Alert>
            )}
            <Stack spacing={0.5} sx={{ mt: 2 }}>
              {requireAccountSelected && (
                <Typography 
                  variant="caption" 
                  color="warning.main"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                >
                  Select a payout account above first
                </Typography>
              )}
              {selectedAccountSummary.startsWith('PayPal') && !paypalSaved && (
                <Typography 
                  variant="caption" 
                  color="warning.main"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                >
                  Save your PayPal account first
                </Typography>
              )}
              {selectedAccountSummary.startsWith('PayPal') && (
                <Typography 
                  variant="caption" 
                  color="info.main"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                >
                  💵 Payments will be processed in USD (as your earnings are displayed in USD)
                </Typography>
              )}
              {missingProfileFields.length > 0 && (
                <Typography 
                  variant="caption" 
                  color="warning.main"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                >
                  Complete your profile before requesting redemptions
                </Typography>
              )}
              {!paymentTermsAccepted && (
                <Typography 
                  variant="caption" 
                  color="warning.main"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                >
                  You must accept the payment terms and liability agreement before requesting redemptions
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* History */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            <History /> Redemption History
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {historyLoading ? (
            <CircularProgress size={24} />
          ) : history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No redemptions yet.</Typography>
          ) : (
            <Stack spacing={1}>
              {history.map((h, idx) => (
                <Box key={idx} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="body2">
                    {h.Date || h.CreatedAt || h.createdAt} • {h.Amount} {h.CurrencyCode || h.Currency}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {h.Status} • {h.PaymentMethodName || h.Method}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Payment Terms & Liability Agreement Dialog */}
      <Dialog
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 700 }}>
          Payment Terms & Liability Agreement
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            Redemption Terms & Conditions
          </Typography>
          <Typography variant="body2" paragraph>
            By requesting redemptions/withdrawals, you acknowledge and agree to the following terms and conditions:
          </Typography>

          <Box sx={{ pl: 2, mb: 3 }}>
            <Typography variant="body2" component="div" paragraph>
              <strong>1. Tax Liability</strong>
              <ul style={{ marginTop: 8, marginBottom: 8 }}>
                <li>You are solely responsible for reporting and paying any taxes applicable to your earnings and redemptions in your jurisdiction.</li>
                <li>The company does not provide tax advice and is not responsible for your tax obligations.</li>
                <li>You must comply with all applicable tax laws and regulations in your country of residence.</li>
                <li>You may be required to provide tax identification information depending on your jurisdiction.</li>
              </ul>
            </Typography>

            <Typography variant="body2" component="div" paragraph>
              <strong>2. Account Responsibility</strong>
              <ul style={{ marginTop: 8, marginBottom: 8 }}>
                <li>You are fully responsible for any illegal activity conducted through your account.</li>
                <li>You will be held liable for any violations of terms of service, laws, or regulations.</li>
                <li>You must maintain the security of your account credentials and notify us immediately of any unauthorized access.</li>
                <li>You are responsible for all activities that occur under your account.</li>
              </ul>
            </Typography>

            <Typography variant="body2" component="div" paragraph>
              <strong>3. Company Limitation of Liability</strong>
              <ul style={{ marginTop: 8, marginBottom: 8 }}>
                <li>The company is not responsible for your tax obligations or any illegal activities associated with your account.</li>
                <li>The company is not liable for any losses, damages, or penalties you may incur due to tax non-compliance.</li>
                <li>The company is not responsible for any legal consequences resulting from illegal activities on your account.</li>
                <li>The company reserves the right to suspend or terminate accounts involved in illegal activities.</li>
              </ul>
            </Typography>

            <Typography variant="body2" component="div" paragraph>
              <strong>4. Withdrawal Processing</strong>
              <ul style={{ marginTop: 8, marginBottom: 8 }}>
                <li>Withdrawal requests are processed within 24-48 hours of approval.</li>
                <li>Processing times may vary based on payment method and country.</li>
                <li>You are responsible for providing accurate payment account information.</li>
                <li>Failed payments due to incorrect account details are your responsibility.</li>
              </ul>
            </Typography>

            <Typography variant="body2" component="div" paragraph>
              <strong>5. Compliance & Legal Requirements</strong>
              <ul style={{ marginTop: 8, marginBottom: 8 }}>
                <li>You must comply with all applicable laws and regulations in your jurisdiction.</li>
                <li>You must not use the platform for any illegal purposes.</li>
                <li>The company may require additional verification or documentation for compliance purposes.</li>
                <li>Failure to comply with legal requirements may result in account suspension or termination.</li>
              </ul>
            </Typography>
          </Box>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Important: By accepting these terms, you acknowledge that you have read, understood, and agree to be bound by all terms and conditions stated above. 
              You understand that you are solely responsible for tax compliance and account activities.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setShowTermsDialog(false)}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
